import React, { useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { Pencil } from 'lucide-react';
import styles from './EditableTitle.module.scss';

interface EditableTitleProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Typography classes (font size/weight/color) applied to the title text. */
  textClassName?: string;
}

/**
 * Inline-editable title matching the reference design: plain (non-editable)
 * text by default, becomes editable only while the pencil icon is clicked —
 * not a plain always-editable input. Commits on blur or Enter.
 */
export const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  onChange,
  placeholder,
  textClassName,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [editing, setEditing] = useState(false);

  const startEditing = () => {
    setEditing(true);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    });
  };

  const commit = () => {
    setEditing(false);
    onChange(ref.current?.textContent?.trim() ?? '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <>
      <span
        ref={ref}
        className={`${styles.titleText} ${textClassName ?? ''}`}
        contentEditable={editing}
        suppressContentEditableWarning
        role="textbox"
        aria-label={placeholder}
        data-placeholder={placeholder}
        onClick={editing ? undefined : startEditing}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      >
        {value}
      </span>
      <Tooltip title="Edit Name">
        <Pencil className={styles.pencil} onClick={startEditing} style={{ width: '1em', height: '1em' }} />
      </Tooltip>
    </>
  );
};

export default EditableTitle;
